import { useMemo } from 'react';
import { useShippingMethodLabelsAndDescriptions } from '@apps/card/hooks/useShippingMethodLabelsAndDescriptions';
import { useStationeryCurrency } from '@shared/utils/currency/useStationeryCurrency';
import { useCurrencyConversionRate } from '@apps/card/hooks/useCurrencyConversionRate';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import PrintPriceCalculator from '@shared/utils/print-price/PrintPriceCalculator';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import { parseOrderCurrencyConversionData } from '@apps/card/utils/orderCurrencyConversion';

/**
 * Reusable hook that returns an instance of PrintPriceCalculator.
 *
 * @param products - Array of StationeryProduct instances
 * @param conversionRateOverride - Optional currency conversion rate override from the order
 * @returns PrintPriceCalculator instance
 */
export const usePrintPrices = (products: ReadonlyArray<StationeryProduct>, conversionRateOverride?: unknown): PrintPriceCalculator => {
  const shippingMethodLabelsAndDescriptions = useShippingMethodLabelsAndDescriptions();
  const currency = useStationeryCurrency();
  const { data: conversionRate } = useCurrencyConversionRate(currency);
  const [isLocalCurrencyEnabled] = useIsFeatureEnabled('printLocalCurrency');

  return useMemo(() => {
    const parsedConversionRateOverride = parseOrderCurrencyConversionData(conversionRateOverride);
    // If conversionRateOverride is provided, use it; otherwise use the geo-location based rate
    const rateToUse = parsedConversionRateOverride ?? (isLocalCurrencyEnabled ? conversionRate : undefined);
    return new PrintPriceCalculator(products, shippingMethodLabelsAndDescriptions, rateToUse);
  }, [products, shippingMethodLabelsAndDescriptions, isLocalCurrencyEnabled, conversionRate, conversionRateOverride]);
};
